import {
  ArrowLeft,
  ExternalLink,
  Link2,
  FolderOpen,
  Lock,
  MessageCircle,
  Search,
  Megaphone,
} from "lucide-react";
import { useCanAccess } from "ra-core";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { BROADCASTS_PATH } from "./broadcasts";
import { CLIENT_STAGES, labelOf } from "../gulaConstants";
import { ChannelIcon } from "../social/ChannelIcon";
import {
  CHANNEL_LABEL,
  mergeInbox,
  type ChannelFilter,
} from "../social/social";
import { SocialWhoIs } from "../social/SocialWhoIs";
import { useSocialInbox } from "../social/useSocial";
import { useAgents } from "../useAgents";
import { filterThreads, shortTime, type InboxFilter } from "./whatsapp";
import { useWaInbox } from "./useWhatsapp";
import { DeleteChatButton } from "./DeleteChatButton";
import { PrivateChatToggle } from "./PrivateChatToggle";
import { usePrivatePhones } from "./usePrivatePhones";
import { WhatsappThread } from "./WhatsappThread";

const FILTERS: { value: InboxFilter; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "unread", label: "Sin leer" },
  { value: "mine", label: "Mis leads" },
];

const CHANNELS: { value: ChannelFilter; label: string }[] = [
  { value: "all", label: "Todo" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "instagram", label: "Instagram" },
  { value: "messenger", label: "Facebook" },
];

const TEMP_DOT: Record<string, string> = {
  hot: "bg-red-500",
  warm: "bg-amber-400",
  cold: "bg-sky-400",
};

/**
 * The team's inbox: WhatsApp (614), Instagram Direct and Messenger of the
 * Gula page, newest first, shared by the whole team.
 */
export const WhatsappPage = () => {
  const { meId, nameOf } = useAgents();
  const { canAccess: isAdmin = false } = useCanAccess({
    resource: "email",
    action: "list",
  });
  const wa = useWaInbox();
  const isPrivate = usePrivatePhones(isAdmin);
  const social = useSocialInbox();
  const threads = mergeInbox(wa.threads, social.threads);
  const isPending = wa.isPending || social.isPending;
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<InboxFilter>("all");
  const [channel, setChannel] = useState<ChannelFilter>("all");
  const [query, setQuery] = useState("");
  // "Cambiar lead" open on a linked Instagram / Facebook conversation
  const [relinking, setRelinking] = useState(false);
  const openId = searchParams.get("chat");
  const visible = filterThreads(
    threads.filter((item) => channel === "all" || item.channel === channel),
    filter,
    query,
    meId,
  );
  const open = threads.find((thread) => thread.key === openId);
  const unreadCount = threads.filter((thread) => thread.unread > 0).length;

  const select = (contactId: string | null) => {
    setRelinking(false);
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (contactId) next.set("chat", contactId);
        else next.delete("chat");
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight">
            <MessageCircle className="h-7 w-7" />
            Mensajes
          </h1>
          <p className="text-sm text-muted-foreground">
            WhatsApp 614 404 301 · Instagram @guladeagua · Facebook ·{" "}
            {unreadCount ? `${unreadCount} sin leer` : "todo leído"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin ? (
            <Link
              to={BROADCASTS_PATH}
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm hover:bg-muted"
            >
              <Megaphone className="h-4 w-4" />
              Difusiones
            </Link>
          ) : null}
          <Link
            to="/whatsapp/material"
            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm hover:bg-muted"
          >
            <FolderOpen className="h-4 w-4" />
            Material
          </Link>
        </div>
      </div>

      <div className="grid h-[calc(100vh-13rem)] min-h-[480px] grid-cols-1 gap-4 md:grid-cols-[340px_1fr]">
        <section
          className={cn(
            "flex min-h-0 flex-col overflow-hidden rounded-3xl border bg-card",
            open && "hidden md:flex",
          )}
        >
          <div className="flex flex-col gap-2 border-b p-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar nombre, teléfono o @usuario"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="rounded-full pl-8"
              />
            </div>
            <div className="flex gap-1">
              {FILTERS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setFilter(item.value)}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs",
                    filter === item.value
                      ? "bg-neutral-900 text-white dark:bg-lime-300 dark:text-neutral-900"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              {CHANNELS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setChannel(item.value)}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px]",
                    channel === item.value
                      ? "border-neutral-900 bg-neutral-100 text-foreground dark:border-lime-300 dark:bg-lime-950/40"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {item.value !== "all" ? (
                    <ChannelIcon channel={item.value} />
                  ) : null}
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto">
            {isPending ? (
              <li className="p-4 text-sm text-muted-foreground">Cargando…</li>
            ) : !visible.length ? (
              <li className="p-4 text-sm text-muted-foreground">
                {threads.length
                  ? "Nada por aquí."
                  : "Aún no hay conversaciones. Aparecerán aquí en cuanto alguien escriba al WhatsApp, al Instagram o a la página de Facebook."}
              </li>
            ) : (
              visible.map((thread) => {
                const isOpen = thread.key === openId;
                return (
                  <li key={thread.key}>
                    <button
                      type="button"
                      onClick={() => select(thread.key)}
                      className={cn(
                        "flex w-full items-center gap-3 border-b px-3 py-2.5 text-left transition-colors",
                        isOpen
                          ? "bg-lime-100 dark:bg-lime-950/40"
                          : "hover:bg-muted/50",
                      )}
                    >
                      <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold">
                        {(thread.name ?? "?")
                          .replace("@", "")
                          .charAt(0)
                          .toUpperCase()}
                        <ChannelIcon
                          channel={thread.channel}
                          className="absolute -bottom-0.5 -right-0.5 border-2 border-card"
                        />
                        <span
                          className={cn(
                            "absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-card",
                            TEMP_DOT[thread.lead_temperature ?? ""] ?? "hidden",
                          )}
                        />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="flex items-center justify-between gap-2">
                          <span
                            className={cn(
                              "truncate text-sm",
                              thread.unread ? "font-semibold" : "font-medium",
                            )}
                          >
                            {thread.channel === "whatsapp" &&
                            isPrivate(thread.handle) ? (
                              <Lock
                                className="mr-1 inline h-3 w-3 text-muted-foreground"
                                aria-label="Solo admins"
                              />
                            ) : null}
                            {thread.name ?? thread.handle ?? "Sin identificar"}
                          </span>
                          <span
                            className={cn(
                              "shrink-0 text-[11px]",
                              thread.unread
                                ? "font-semibold text-lime-700"
                                : "text-muted-foreground",
                            )}
                          >
                            {shortTime(thread.last_message_at)}
                          </span>
                        </span>
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate text-xs text-muted-foreground">
                            {thread.unlinked ? (
                              <span className="mr-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900 dark:bg-amber-400/15 dark:text-amber-100">
                                ¿Quién es?
                              </span>
                            ) : null}
                            {thread.last_direction === "out" ? "Tú: " : ""}
                            {thread.last_preview}
                          </span>
                          {thread.unread ? (
                            <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-lime-500 px-1.5 text-[11px] font-bold text-white">
                              {thread.unread}
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </section>

        <section
          className={cn(
            "flex min-h-0 flex-col gap-2",
            !open && "hidden md:flex",
          )}
        >
          {open ? (
            <>
              <div className="flex items-center gap-3 rounded-3xl border bg-card px-4 py-3">
                <button
                  type="button"
                  onClick={() => select(null)}
                  className="md:hidden"
                  aria-label="Volver"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-semibold">
                    {open.name ?? open.handle ?? "Sin identificar"}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {[
                      CHANNEL_LABEL[open.channel],
                      open.handle !== open.name ? open.handle : null,
                      open.stage ? labelOf(CLIENT_STAGES, open.stage) : null,
                      open.sales_id
                        ? `de ${nameOf(open.sales_id).split(" ")[0]}`
                        : "sin coger",
                      open.city,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
                {isAdmin && open.channel === "whatsapp" && open.handle ? (
                  <PrivateChatToggle
                    phone={open.handle}
                    name={open.name}
                    isPrivate={isPrivate(open.handle)}
                  />
                ) : null}
                {isAdmin ? (
                  <DeleteChatButton
                    key={`delete-${open.key}`}
                    name={open.name ?? open.handle ?? "este contacto"}
                    waContactId={
                      open.channel === "whatsapp" ? open.contactId : null
                    }
                    socialThreadId={
                      open.channel === "whatsapp" ? null : open.threadId
                    }
                    clientId={open.client_id}
                    onDeleted={() => {
                      select(null);
                      wa.reload();
                      social.reload();
                    }}
                  />
                ) : null}
                {open.threadId != null && !open.unlinked && !relinking ? (
                  <button
                    type="button"
                    onClick={() => setRelinking(true)}
                    className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium hover:bg-muted"
                    title="Esta conversación es de otro lead"
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    Cambiar lead
                  </button>
                ) : null}
                {open.client_id ? (
                  <Link
                    to={`/clientes?cliente=${open.client_id}`}
                    className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium hover:bg-muted"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Ficha
                  </Link>
                ) : null}
              </div>
              {open.threadId != null && (open.unlinked || relinking) ? (
                <SocialWhoIs
                  key={`who-${open.key}`}
                  threadId={open.threadId}
                  onDone={() => {
                    setRelinking(false);
                    social.reload();
                  }}
                  onClose={
                    open.unlinked ? undefined : () => setRelinking(false)
                  }
                />
              ) : null}
              <WhatsappThread
                key={open.key}
                channel={open.channel}
                contactId={open.contactId}
                threadId={open.threadId}
                phone={open.channel === "whatsapp" ? open.handle : null}
                firstName={open.name?.split(" ")[0]}
                className="flex-1"
              />
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-3xl border border-dashed text-sm text-muted-foreground">
              <MessageCircle className="h-8 w-8" />
              Elige una conversación
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

WhatsappPage.path = "/whatsapp";
